import { memo } from 'react';
import { cn } from '@/lib/utils';
import { analytics } from '@/services/analytics';

export type CanvasStepStatus = 'current' | 'complete' | 'partial' | 'upcoming';

export interface CanvasStepDefinition {
  readonly id: string;
  readonly label: string;
  readonly status: CanvasStepStatus;
  readonly disabled?: boolean;
  readonly meta?: {
    readonly remainingCount?: number;
  };
}

interface CanvasStepRibbonProps {
  readonly steps: readonly CanvasStepDefinition[];
  readonly onStepSelect?: (step: CanvasStepDefinition) => void;
}

export const CanvasStepRibbon = memo(({ steps, onStepSelect }: CanvasStepRibbonProps) => {
  return (
    <nav
      aria-label="Order progress"
      className="max-w-full overflow-x-auto pb-1"
    >
      <ol className="flex items-stretch gap-0 whitespace-nowrap pr-2">
        {steps.map((step, index) => {
          const statusClass =
            step.status === 'complete'
              ? 'bg-emerald-500 text-white border border-emerald-600/80 shadow-sm outline outline-1 outline-emerald-500/40 dark:text-emerald-50'
              : step.status === 'partial'
                ? 'bg-emerald-200 text-emerald-900 border border-emerald-300 shadow-sm outline outline-1 outline-emerald-300/60 dark:bg-emerald-600/60 dark:text-emerald-50 dark:border-emerald-500/70'
                : step.status === 'current'
                  ? 'bg-primary text-primary-foreground border border-primary-500/80 shadow outline outline-1 outline-primary/40'
                  : 'bg-muted/70 text-foreground/80 border border-border/60 shadow-sm backdrop-blur-sm outline outline-1 outline-border/40';

          const zIndex = steps.length - index;

          const isActive = step.status === 'current';
          const isDisabled = Boolean(step.disabled);
          const handleClick = () => {
            if (onStepSelect && !isDisabled) {
              analytics.track('button: ui.step_ribbon_select', {
                stepId: step.id,
                stepLabel: step.label,
                stepIndex: index,
                status: step.status,
              });
              onStepSelect(step);
            }
          };

          return (
            <li
              key={step.id}
              className={cn(
                'relative flex shrink-0 grow-0 basis-auto items-stretch overflow-visible',
                index > 0 && '-ml-4 sm:-ml-5'
              )}
              style={{ zIndex }}
            >
              <button
                type="button"
                className={cn(
                  'relative flex min-h-[30px] w-full items-center justify-end pl-3 pr-0.5 text-[0.68rem] font-semibold uppercase tracking-[0.12em] transition-colors duration-200 text-right sm:min-h-[34px] sm:pl-4 sm:pr-1.5 sm:text-[0.7rem] after:content-"" after:absolute after:top-0 after:right-[-8px] after:h-full after:w-[8px] after:bg-inherit after:[clip-path:polygon(0_0,100%_50%,0_100%)] after:outline after:outline-1 after:outline-current/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-default disabled:opacity-80',
                  statusClass,
                  index < steps.length - 1 ? 'after:block' : 'after:hidden',
                  index > 0 && 'pl-6 sm:pl-8',
                  onStepSelect && !isActive && !isDisabled ? 'cursor-pointer hover:brightness-[1.05]' : 'cursor-default opacity-90'
                )}
                aria-current={isActive ? 'step' : undefined}
                title={step.label}
                onClick={onStepSelect ? handleClick : undefined}
                disabled={!onStepSelect || isActive || isDisabled}
              >
                <span className="ml-auto flex h-full items-center justify-end gap-2 max-w-[7.5rem] truncate text-sm font-semibold tracking-tight text-right leading-none sm:max-w-[8.5rem] sm:text-[0.72rem]">
                  <span className="truncate leading-none">{step.label}</span>
                  {typeof step.meta?.remainingCount === 'number' && step.meta.remainingCount > 0 ? (
                    <span className="inline-flex min-h-[1.2rem] min-w-[1.35rem] items-center justify-center rounded-full bg-background/90 px-2 py-[1px] text-[0.75rem] font-bold text-foreground leading-none shadow-sm outline outline-1 outline-border/40">
                      {step.meta.remainingCount}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
});

CanvasStepRibbon.displayName = 'CanvasStepRibbon';
